import React from "react";
import * as Icons from "lucide-react";
import { BADGE_META, TIER_META, METRIC_UNIT } from "@/lib/badges";
import { cn } from "@/lib/utils";

function TrophyMedal({ earned, tier, Icon }) {
  if (!earned) {
    return (
      <span className="relative grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-muted text-muted-foreground">
        <Icon className="h-6 w-6 opacity-40" />
        <span className="absolute -bottom-1 -right-1 grid h-6 w-6 place-items-center rounded-full border bg-card"><Icons.Lock className="h-3 w-3" /></span>
      </span>
    );
  }
  return (
    <span className={cn("grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-gradient-to-br text-white shadow-lg", tier.grad)}>
      <Icon className="h-7 w-7 drop-shadow" strokeWidth={2.2} />
    </span>
  );
}

export function BadgeCard({ b, lang }) {
  const L = lang === "id" ? "id" : "en";
  const meta = BADGE_META[b.key] || { icon: "Trophy", id: { t: b.key, d: "" }, en: { t: b.key, d: "" } };
  const copy = meta[L];
  const tier = TIER_META[b.tier] || TIER_META.bronze;
  const Icon = Icons[meta.icon] || Icons.Trophy;
  const pct = Math.min(100, Math.round((b.value / b.threshold) * 100));
  const locale = L === "id" ? "id-ID" : "en-US";
  const unit = METRIC_UNIT[b.metric]?.[L] || "";
  const date = b.earned_at && new Date(`${b.earned_at}T00:00:00`).toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" });

  return (
    <div data-testid={`badge-${b.key}`} data-earned={b.earned}
      className={cn("rounded-2xl border p-5 transition-[transform,border-color] duration-200 hover:-translate-y-0.5", b.earned ? tier.soft : "bg-card")}>
      <div className="flex items-start gap-4">
        <TrophyMedal earned={b.earned} tier={tier} Icon={Icon} />
        <div className="min-w-0 flex-1">
          <span className={cn("text-[10px] font-bold uppercase tracking-wider", b.earned ? tier.text : "text-muted-foreground")}>{tier[L]}</span>
          <div className="font-display font-bold leading-tight">{copy.t}</div>
          <div className="mt-0.5 text-xs text-muted-foreground">{copy.d}</div>
        </div>
      </div>
      {b.earned ? (
        <div className={cn("mt-4 flex items-center gap-1.5 text-xs font-medium", tier.text)} data-testid={`badge-${b.key}-earned`}>
          <Icons.CheckCircle2 className="h-3.5 w-3.5" />
          {L === "id" ? "Didapat" : "Earned"}{date ? ` · ${date}` : ""}
        </div>
      ) : (
        <div className="mt-4" data-testid={`badge-${b.key}-progress`}>
          <div className="h-1.5 overflow-hidden rounded-full bg-muted">
            <div className={cn("h-full rounded-full opacity-70 transition-[width] duration-500", tier.bar)} style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-1 text-right text-[11px] tabular-nums text-muted-foreground">
            {b.value.toLocaleString(locale)}/{b.threshold.toLocaleString(locale)} {unit}
          </div>
        </div>
      )}
    </div>
  );
}
